import { Control } from '@peckadesign/pd-naja/dist/types'

declare global {
	interface Window {
		// GTM Adjust Smart Script tag emituje do dataLayer hotový (obohacený) Adjust link.
		dataLayer?: unknown[]
	}
}

const SELECTOR = 'a[href]'
const PROCESSED_ATTR = 'adjustProcessed'

// Linky jsou připravené k vložení do href (link nese klíč `sz_adjust_link`).
const READY_EVENT = 'sz_adjust_link_ready'
// Linky z nějakého důvodu nevznikly – necháváme statický href z latte jako fallback.
const FAILED_EVENT = 'sz_adjust_link_failed'

// GTM Adjust tag pushne událost do dataLayer až po souhlasu (`ad_storage`) a dotažení fbpid.
// Protože si gtm.js `dataLayer.push` přepisuje na vlastní funkci, není spolehlivé se na push
// „navěsit" (náš wrapper by gtm.js osiřel). Místo toho čteme pole napřímo – do něj se pushnuté
// záznamy vždy přidávají – dokud událost nedorazí, nebo nevyprší limit. Po vyřešení se ruší.
const POLL_INTERVAL_MS = 250
const POLL_TIMEOUT_MS = 20000

// Hosty, které vedou ke stažení appky (App Store / Google Play). Adjust odkazy se poznají podle
// suffixu `.go.link` (superzoo.go.link a případné další brandované subdomény).
const APP_DOWNLOAD_HOSTS = ['apps.apple.com', 'itunes.apple.com', 'play.google.com']
const APP_DOWNLOAD_HOST_SUFFIX = '.go.link'

const CLICK_EVENT = 'sz_adjust_link_click'

/**
 * Zjistí, zda odkaz vede na stažení mobilní appky – podle hostname (přesná shoda, ne substring),
 * aby se náhodou nechytila jiná URL jen proto, že obsahuje podřetězec.
 */
function isAppDownloadUrl(href: string): boolean {
	let url: URL

	try {
		url = new URL(href, location.href)
	} catch {
		return false
	}

	const { hostname } = url

	return hostname.endsWith(APP_DOWNLOAD_HOST_SUFFIX) || APP_DOWNLOAD_HOSTS.includes(hostname)
}

/**
 * Určí umístění odkazu pro dataLayer (sz_adjust_link_placement) – buď explicitně z `data-adjust-placement`
 * (naše latte tlačítka), nebo odvozením z kontextu, jinak `other`. Info lišta se testuje dřív než obsah
 * z editoru – notice má na sobě obě třídy.
 */
function resolvePlacement(anchor: HTMLAnchorElement): string {
	if (anchor.dataset.adjustPlacement) {
		return anchor.dataset.adjustPlacement
	}

	if (anchor.closest('.top-notices-bar')) {
		return 'info_strip'
	}

	if (anchor.closest('.content-editor')) {
		return 'wysiwyg'
	}

	return 'other'
}

// Delegovaný click listener se registruje jen jednou za celou stránku (ne v initialize() controlu –
// ten se volá znovu pro každý AJAX snippet a listener by se navěsil vícekrát).
let clickListenerBound = false

function bindClickListener(): void {
	if (clickListenerBound) {
		return
	}

	clickListenerBound = true

	document.addEventListener('click', (event) => {
		const target = event.target

		if (!(target instanceof Element)) {
			return
		}

		const anchor = target.closest<HTMLAnchorElement>('a[href]')

		// Kromě odkazů na store bereme i ty, kterým jsme href už přepsali – obohacený Adjust link
		// nemusí být na `.go.link` hostu a jinak by nám takový klik z měření vypadl.
		if (!anchor || !(isAppDownloadUrl(anchor.href) || anchor.dataset[PROCESSED_ATTR] === 'true')) {
			return
		}

		window.dataLayer ??= []
		window.dataLayer.push({
			event: CLICK_EVENT,
			sz_adjust_link_placement: resolvePlacement(anchor),
			sz_adjust_link: anchor.href
		})
	})
}

class AdjustSmartLinksControl implements Control {
	private link: string | null = null
	private resolved = false
	private polling = false

	public initialize(context: Element | Document): void {
		bindClickListener()

		// Link už máme (z dřívějška / kvůli jinému snippetu) → aplikuj na kotvy v tomto kontextu
		// a končíme; žádné čtení dataLayer už není potřeba.
		if (this.link) {
			this.applyLink(context)

			return
		}

		// Jinak (i opakovaně při AJAX snippetech, pokud předchozí čtení vypršelo – např. souhlas
		// padl později) spustíme čtení dataLayer. Po explicitním failu už nečteme.
		if (!this.resolved && !this.polling) {
			this.pollDataLayer()
		}
	}

	private pollDataLayer(): void {
		this.polling = true

		// Událost už mohla proběhnout dřív, než jsme se dostali ke slovu.
		if (this.readDataLayer()) {
			this.polling = false
			return
		}

		const deadline = Date.now() + POLL_TIMEOUT_MS
		const timer = window.setInterval(() => {
			if (this.readDataLayer() || Date.now() >= deadline) {
				window.clearInterval(timer)
				this.polling = false
			}
		}, POLL_INTERVAL_MS)
	}

	// Projde dataLayer. Vrací true, jakmile je věc vyřešená (link je k dispozici, nebo přišel
	// explicitní fail) – pak už nemá smysl číst dál.
	private readDataLayer(): boolean {
		for (const entry of window.dataLayer ?? []) {
			if (entry === null || typeof entry !== 'object') {
				continue
			}

			const { event, sz_adjust_link: link } = entry as Record<string, unknown>

			if (event === READY_EVENT && typeof link === 'string' && link) {
				this.link = link
				this.resolved = true
				this.applyLink(document)

				return true
			}

			if (event === FAILED_EVENT) {
				// Link nevznikl – statický href z latte je fallback, neděláme tedy nic.
				this.resolved = true

				return true
			}
		}

		return false
	}

	private applyLink(context: Element | Document): void {
		if (!this.link) {
			return
		}

		// Bereme všechny odkazy vedoucí na stažení appky – naše latte tlačítka i odkazy vložené
		// klientem do WYSIWYG obsahu. Rozhoduje hostname, ne značka v šabloně.
		context.querySelectorAll<HTMLAnchorElement>(SELECTOR).forEach((anchor) => {
			if (!isAppDownloadUrl(anchor.href)) {
				return
			}

			// Idempotence – statický href z latte funguje i bez JS / bez souhlasu, tady ho jen
			// jednou přepíšeme lepším atribuovaným Adjust linkem.
			if (anchor.dataset[PROCESSED_ATTR] !== 'true') {
				anchor.href = this.link as string
				anchor.dataset[PROCESSED_ATTR] = 'true'
			}
		})
	}
}

export default new AdjustSmartLinksControl()
